import { useEffect, useRef } from 'react'
import { useMapboxMapContext } from '../contexts/MapboxMapContext'
import { deepEqual } from '@/shared/utils/deepEqual'

export interface UseWaypointLayerProps {
  waypoints: any[]
  onWaypointClick?: (waypoint: any) => void
  selectedWaypoint?: any // Exclude selected waypoint from regular markers to avoid double markers
}

/**
 * Hook for managing waypoint layer rendering
 * Handles waypoint GeoJSON source and layer creation/updates
 */
export function useWaypointLayer({
  waypoints,
  onWaypointClick,
  selectedWaypoint
}: UseWaypointLayerProps) {
  const { map, isLoaded, styleLoaded } = useMapboxMapContext()
  const onWaypointClickRef = useRef(onWaypointClick)
  const waypointHandlersRef = useRef<{
    mouseenter?: () => void
    mouseleave?: () => void
    click?: (e: any) => void
  }>({})
  
  // Stable reference to prevent unnecessary layer updates
  const stableWaypointsRef = useRef<any[]>([])
  const previousWaypointsRef = useRef<any[]>([])
  const previousFeatureSignatureRef = useRef<string>('')

  // Update ref when callback changes
  useEffect(() => {
    onWaypointClickRef.current = onWaypointClick
  }, [onWaypointClick])

  useEffect(() => {
    // Deep equality check - only update if waypoints actually changed
    if (deepEqual(previousWaypointsRef.current, waypoints)) {
      return // Skip update - no changes
    }
    
    // Update stable ref
    previousWaypointsRef.current = waypoints
    stableWaypointsRef.current = waypoints
    
    if (!map.current || !isLoaded || !styleLoaded) {
      return
    }

    // Trust the styleLoaded prop - it's more reliable than map.current.isStyleLoaded()
    // which can lag or return false even when the style is ready
    if (!styleLoaded) {
      // Listen for style load event as backup
      if (map.current) {
        map.current.once('styledata', () => {
          // Style loaded, will retry
        })
      }
      return
    }
    
    // If styleLoaded is true, proceed with rendering
    // The MapboxMap component tracks this state correctly, so we can trust it
    // map.current.isStyleLoaded() may return false due to timing, but that's okay

    // Use stable waypoints for processing
    const waypointsToProcess = stableWaypointsRef.current
    
    // Get selected waypoint ID to exclude it from regular markers
    // This prevents double markers - selected waypoint has its own marker
    const selectedWaypointId = selectedWaypoint?.id
    
    const labelLayerId = 'waypoint-cluster-labels'

    // Filter out waypoints without valid coordinates
    // Also exclude the selected waypoint to avoid double markers
    const waypointFeatures = waypointsToProcess
      .filter(waypoint => {
        if (selectedWaypointId && waypoint.id === selectedWaypointId) {
          return false
        }

        const lat = waypoint.latitude ?? waypoint.lat ?? waypoint.geom?.coordinates?.[1]
        const lng = waypoint.longitude ?? waypoint.lon ?? waypoint.geom?.coordinates?.[0]

        const hasValidCoords =
          lat != null &&
          lng != null &&
          !isNaN(Number(lat)) &&
          !isNaN(Number(lng))

        if (!hasValidCoords && waypoint.id) {
        }

        return hasValidCoords
      })
      .map(waypoint => {
        let lat: number | undefined
        let lng: number | undefined

        if (waypoint.geom && Array.isArray(waypoint.geom.coordinates)) {
          lng = waypoint.geom.coordinates[0]
          lat = waypoint.geom.coordinates[1]
        }

        lat = lat ?? waypoint.latitude ?? waypoint.lat
        lng = lng ?? waypoint.longitude ?? waypoint.lon

        if (lat == null || lng == null || isNaN(Number(lat)) || isNaN(Number(lng))) {
        }

        const isCluster = waypoint.is_cluster === true
        const clusterCount =
          isCluster && typeof waypoint.cluster_count === 'number'
            ? waypoint.cluster_count
            : 0
        const featureId =
          (isCluster ? waypoint.cluster_id : waypoint.id) ??
          waypoint.id ??
          `cluster-${Math.random().toString(36).slice(2)}`

        const properties: Record<string, any> = {
          id: featureId,
          name: waypoint.name,
          created_at: waypoint.created_at,
          poi_type: isCluster ? 'cluster' : waypoint.poi_type || 'default',
          folder_color: isCluster
            ? null
            : waypoint.folder?.color || (waypoint as any).waypoint_folders?.color || null,
          source: isCluster ? 'cluster' : waypoint.source || 'manual',
          is_cluster: isCluster,
          cluster_count: clusterCount,
          cluster_id: isCluster ? featureId : null
        }

        if (!isCluster) {
          properties.description = waypoint.description
        }

        return {
          id: featureId,
          type: 'Feature' as const,
          properties,
          geometry: {
            type: 'Point' as const,
            coordinates: [Number(lng), Number(lat)] as [number, number]
          }
        }
      })

    const geoJsonData = {
      type: 'FeatureCollection' as const,
      features: waypointFeatures
    }

    if (waypointsToProcess.length > 0 && waypointFeatures.length === 0) {
    }

    try {
      // We've already checked styleLoaded above, so we can proceed
      // The map style should be ready if styleLoaded is true

      const nonClusterColorExpression: any = [
        'coalesce',
        ['get', 'folder_color'],
        [
          'match',
          ['get', 'source'],
          'gpx_import', '#3b82f6',
          'manual', '#f59e0b',
          'campsite_discovery', '#10b981',
          'poi_search', '#8b5cf6',
          [
            'match',
            ['get', 'poi_type'],
            'meetup_spot', '#3b82f6',
            'campsite', '#10b981',
            '#f59e0b'
          ]
        ]
      ]

      const circleColorExpression: any = [
        'case',
        ['==', ['get', 'is_cluster'], true],
        '#5C8DC5',
        nonClusterColorExpression
      ]

      const nonClusterRadiusExpression: any = [
        'match',
        ['get', 'poi_type'],
        'meetup_spot', 8,
        'campsite', 8,
        6
      ]

      const clusterRadiusExpression: any = [
        'interpolate',
        ['linear'],
        ['coalesce', ['get', 'cluster_count'], 1],
        1, 14,
        5, 18,
        10, 22,
        25, 28,
        50, 34
      ]

      const circleRadiusExpression: any = [
        'case',
        ['==', ['get', 'is_cluster'], true],
        clusterRadiusExpression,
        nonClusterRadiusExpression
      ]

      const circleStrokeWidthExpression: any = [
        'case',
        ['==', ['get', 'is_cluster'], true],
        3,
        2
      ]

      const circleOpacityExpression: any = [
        'case',
        ['==', ['get', 'is_cluster'], true],
        0.92,
        1
      ]

      const waypointLayerStyle: mapboxgl.AnyLayer = {
        id: 'waypoint-markers',
        type: 'circle',
        source: 'waypoint-markers',
        paint: {
          'circle-color': circleColorExpression,
          'circle-radius': circleRadiusExpression,
          'circle-stroke-color': '#ffffff',
          'circle-stroke-width': circleStrokeWidthExpression,
          'circle-opacity': circleOpacityExpression
        }
      }

      const ensureClusterLabelLayer = () => {
        if (!map.current?.getSource('waypoint-markers')) {
          return
        }

        if (!map.current.getLayer(labelLayerId)) {
          map.current.addLayer({
            id: labelLayerId,
            type: 'symbol',
            source: 'waypoint-markers',
            filter: ['==', ['get', 'is_cluster'], true],
            layout: {
              'text-field': ['to-string', ['get', 'cluster_count']],
              'text-size': 12,
              'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
              'text-offset': [0, 0.6],
              'text-allow-overlap': true
            },
            paint: {
              'text-color': '#FFFFFF',
              'text-halo-color': '#1F2937',
              'text-halo-width': 0.8
            }
          })
        }

        if (map.current.getLayer(labelLayerId)) {
          map.current.moveLayer(labelLayerId)
        }
      }

      const hasSource = map.current.getSource('waypoint-markers')
      const hasLayer = map.current.getLayer('waypoint-markers')
      const hasFeatures = waypointFeatures.length > 0
      const featureSignature = hasFeatures
        ? waypointFeatures
            .map(feature => {
              const props = (feature.properties ?? {}) as Record<string, any>
              const coords = (feature.geometry as any)?.coordinates ?? []
              const identifier = props.cluster_id ?? props.id ?? feature.id ?? ''
              const lat = coords[1] ?? ''
              const lng = coords[0] ?? ''
              const count = props.cluster_count ?? ''
              return `${identifier}:${lat}:${lng}:${count}`
            })
            .join('|')
        : '__empty__'
      const dataToSet =
        hasFeatures
          ? geoJsonData
          : { type: 'FeatureCollection' as const, features: [] }
      
      if (hasSource) {
        // Guard: Only update if we have features or if waypoints array is actually empty
        // This prevents clearing map data when waypoints exist but are temporarily filtered
        const hasWaypoints = stableWaypointsRef.current.length > 0

        // Only update if:
        // 1. We have features to show, OR
        // 2. Waypoints array is empty (explicit clear)
        if (hasFeatures || !hasWaypoints) {
          const waypointSource = map.current.getSource('waypoint-markers') as mapboxgl.GeoJSONSource
          const shouldUpdateData = featureSignature !== previousFeatureSignatureRef.current

          if (shouldUpdateData) {
            waypointSource.setData(dataToSet)
            previousFeatureSignatureRef.current = featureSignature
          }
        }
        
        // Re-add layer if it was removed
        if (!hasLayer) {
          map.current.addLayer(waypointLayerStyle)
        }
        
        // Ensure waypoint markers stay on top
        if (map.current.getLayer('waypoint-markers')) {
          const layersToMoveAbove = ['route-lines']
          layersToMoveAbove.forEach(layerId => {
            if (map.current?.getLayer(layerId)) {
              map.current.moveLayer('waypoint-markers', layerId)
            }
          })
        }

        ensureClusterLabelLayer()
        
        // Ensure layer is visible
        map.current.setLayoutProperty('waypoint-markers', 'visibility', 'visible')
      } else {
        // Create new source and layer
        map.current.addSource('waypoint-markers', {
          type: 'geojson',
          data: dataToSet
        })

        map.current.addLayer(waypointLayerStyle)
        ensureClusterLabelLayer()
        previousFeatureSignatureRef.current = featureSignature

        // Ensure waypoint markers are always on top
        const layersToMoveAbove = ['route-lines']
        layersToMoveAbove.forEach(layerId => {
          if (map.current?.getLayer(layerId)) {
            map.current.moveLayer('waypoint-markers', layerId)
          }
        })
        
        // Ensure layer is visible
        map.current.setLayoutProperty('waypoint-markers', 'visibility', 'visible')
      }
      
      // Note: Clearing logic is now handled above with guards to prevent unnecessary clearing

      // Always re-attach click handlers if layer exists (handlers may be lost when layer is recreated)
      if (map.current.getLayer('waypoint-markers')) {
        // Remove any existing handlers first to avoid duplicates
        if (waypointHandlersRef.current.mouseenter) {
          map.current.off('mouseenter', 'waypoint-markers', waypointHandlersRef.current.mouseenter)
        }
        if (waypointHandlersRef.current.mouseleave) {
          map.current.off('mouseleave', 'waypoint-markers', waypointHandlersRef.current.mouseleave)
        }
        if (waypointHandlersRef.current.click) {
          map.current.off('click', 'waypoint-markers', waypointHandlersRef.current.click)
        }

        // Create new handler functions
        waypointHandlersRef.current.mouseenter = () => {
          if (map.current) {
            map.current.getCanvas().style.cursor = 'pointer'
          }
        }

        waypointHandlersRef.current.mouseleave = () => {
          if (map.current) {
            map.current.getCanvas().style.cursor = ''
          }
        }

        waypointHandlersRef.current.click = (e: any) => {
          try {
            if (e.originalEvent) {
              e.originalEvent.stopPropagation()
              e.originalEvent.preventDefault()
            }
            if (e.stopPropagation) {
              e.stopPropagation()
            }
            
            const feature = e.features && (e.features[0] as any)
            if (!feature) {
              return
            }

            const props = feature.properties || {}
            const coords = feature.geometry?.coordinates || []
            const isCluster =
              props.is_cluster === true ||
              props.is_cluster === 'true' ||
              props.poi_type === 'cluster'

            if (isCluster) {
              if (map.current) {
                const lng = Array.isArray(coords) ? Number(coords[0]) : Number(props.longitude ?? 0)
                const lat = Array.isArray(coords) ? Number(coords[1]) : Number(props.latitude ?? 0)
                const currentZoom = map.current.getZoom()
                map.current.easeTo({
                  center: { lng, lat },
                  zoom: Math.min(currentZoom + 2, 16)
                })
              }
              return
            }

            if (onWaypointClickRef.current) {
              const waypointData = {
                ...props,
                longitude: Array.isArray(coords) ? Number(coords[0]) : props.longitude,
                latitude: Array.isArray(coords) ? Number(coords[1]) : props.latitude
              }
              onWaypointClickRef.current(waypointData)
            }
          } catch (error) {
            if (map.current?.isStyleLoaded()) {
            }
          }
        }

        // Attach handlers
        map.current.on('mouseenter', 'waypoint-markers', waypointHandlersRef.current.mouseenter)
        map.current.on('mouseleave', 'waypoint-markers', waypointHandlersRef.current.mouseleave)
        map.current.on('click', 'waypoint-markers', waypointHandlersRef.current.click)
      }
    } catch (error) {
      if (map.current?.isStyleLoaded()) {
      }
    }
    // Note: waypoints dependency is checked via deep equality above
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, isLoaded, styleLoaded, waypoints, selectedWaypoint])
}

